import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'
import { Material } from '../types'
import MaterialCard from '../components/MaterialCard'
import UploadZone from '../components/UploadZone'
import LoadingSpinner from '../components/LoadingSpinner'
import { AlertCircle, CheckCircle, FolderOpen, Layers, ArrowRight } from 'lucide-react'
import { useWebSocket } from '../hooks/useWebSocket'

export default function MaterialsPage() {
  const [materials, setMaterials] = useState<Material[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useWebSocket({
    onMessage: (wsMessage) => {
      if (!wsMessage.material_id) return
      setMaterials((prev) =>
        prev.map((mat) => {
          if (mat.materialId !== wsMessage.material_id) return mat
          const updated = { ...mat }
          switch (wsMessage.type) {
            case 'processing_started':
              updated.summary_status = 'processing'
              updated.quiz_status = 'processing'
              updated.mindmap_status = 'processing'
              break
            case 'summary_ready':
              updated.summary_status = 'ready'
              updated.summary_content = wsMessage.content
              updated.summary_generated_at = new Date().toISOString()
              break
            case 'quiz_ready':
              updated.quiz_status = 'ready'
              updated.quiz_content = wsMessage.content
              updated.quiz_generated_at = new Date().toISOString()
              break
            case 'mindmap_ready':
              updated.mindmap_status = 'ready'
              updated.mindmap_content = wsMessage.content
              updated.mindmap_generated_at = new Date().toISOString()
              break
            case 'summary_failed':
              updated.summary_status = 'failed'
              updated.summary_error = wsMessage.error
              break
            case 'quiz_failed':
              updated.quiz_status = 'failed'
              updated.quiz_error = wsMessage.error
              break
            case 'mindmap_failed':
              updated.mindmap_status = 'failed'
              updated.mindmap_error = wsMessage.error
              break
          }
          return updated
        })
      )
    },
    onConnect: () => {},
    onDisconnect: () => {},
  })

  useEffect(() => { loadMaterials() }, [])

  // Poll while any material is still processing (fallback for missed WS messages)
  useEffect(() => {
    const hasProcessing = materials.some(
      (m) =>
        m.summary_status === 'processing' ||
        m.quiz_status === 'processing' ||
        m.mindmap_status === 'processing'
    )
    if (!hasProcessing) return
    const timer = setInterval(loadMaterials, 5000)
    return () => clearInterval(timer)
  }, [materials])

  const loadMaterials = async () => {
    try {
      const data = await api.getMaterials() as { materials: Material[] }
      setMaterials(data.materials || [])
    } catch {
      setMessage({ type: 'error', text: 'Error al cargar materiales' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpload = async (file: File) => {
    setIsUploading(true)
    setMessage(null)
    try {
      const result = await api.uploadMaterial(file)
      await api.processMaterial(result.materialId)
      await loadMaterials()
      setMessage({ type: 'success', text: 'Material subido. El contenido se está generando automáticamente.' })
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
      const msg = typeof detail === 'object' && detail?.message
        ? detail.message
        : typeof detail === 'string'
        ? detail
        : 'Error al subir el material. Por favor intenta de nuevo.'
      setMessage({ type: 'error', text: msg })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (materialId: string) => {
    if (!confirm('¿Estás seguro de eliminar este material?')) return
    try {
      await api.deleteMaterial(materialId)
      setMaterials((prev) => prev.filter((m) => m.materialId !== materialId))
      setMessage({ type: 'success', text: 'Material eliminado.' })
    } catch {
      setMessage({ type: 'error', text: 'Error al eliminar el material.' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando materiales..." />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-[1280px]">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Mis Materiales</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            Sube y gestiona tus archivos de estudio
          </p>
        </div>
        <Link
          to="/courses"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
          style={{
            background: 'rgba(79,70,229,0.12)',
            color: '#A5B4FC',
            border: '1px solid rgba(99,102,241,0.25)',
          }}
        >
          <Layers className="w-4 h-4" />
          Agrupa en cursos
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Feedback banner */}
      {message && (
        <div
          className="flex items-start gap-3 p-4 rounded-xl text-sm animate-slide-up"
          style={{
            background: message.type === 'success' ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}`,
            color: message.type === 'success' ? '#34D399' : '#FB7185',
          }}
        >
          {message.type === 'success'
            ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          }
          {message.text}
        </div>
      )}

      {/* Upload zone */}
      <UploadZone
        onUpload={handleUpload}
        onError={(msg) => setMessage({ type: 'error', text: msg })}
        isUploading={isUploading}
      />

      {/* Materials list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>
            Materiales{' '}
            <span
              className="text-sm font-normal ml-1 px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#64748B' }}
            >
              {materials.length}
            </span>
          </h2>
        </div>

        {materials.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(79,70,229,0.1)' }}
            >
              <FolderOpen className="w-7 h-7" style={{ color: '#4F46E5' }} />
            </div>
            <p className="font-medium mb-1" style={{ color: '#E2E8F0' }}>
              Sin materiales aún
            </p>
            <p className="text-sm" style={{ color: '#64748B' }}>
              Sube tu primer archivo para comenzar
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {materials.map((material) => (
              <MaterialCard
                key={material.materialId}
                material={material}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
